import React, { FC, useCallback, useContext, useMemo } from 'react';

import { Descendant } from 'slate';

//constants
import ICONS from '../../../../../constants/icons';

//context
import { EditorContext } from '../../../../../context/EditorContext';

//hooks
import { useLockFormulaWarning } from '../../../../../hooks/formulas/useLockFormulaWarning';
import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';

//components
import FormulaEditorToolbarIcon from './FormulaEditorToolbarIcon';

interface FormulaLockUnlockMainProps {
    isEditable?: boolean;
}

const FormulaLockUnlockMain: FC<FormulaLockUnlockMainProps> = ({
    isEditable,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { hasWarning } = useLockFormulaWarning();
    const messageContentUpdate = useMessageContentGetAndUpdate();
    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );

    const showLockUnLockIcon = useMemo<boolean>(() => {
        if (!formula?.structure) return false;

        const variantTypeSet = new Set<string>();

        const checkChildrenForDuplicateVariants = (children: Descendant[]) => {
            for (const child of children) {
                if ('variantType' in child && child.variantType) {
                    if (variantTypeSet.has(child.variantType)) {
                        return true;
                    }
                    variantTypeSet.add(child.variantType);
                } else if (
                    'children' in child &&
                    child.children &&
                    Array.isArray(child.children)
                ) {
                    // Recursively check nested children
                    if (checkChildrenForDuplicateVariants(child.children)) {
                        return true;
                    }
                }
            }
            return false;
        };

        if (checkChildrenForDuplicateVariants(formula.structure)) {
            return true;
        }

        return false;
    }, [formula]);

    const isDisabled = useMemo<boolean>(() => {
        return !formula?.id || !isEditable;
    }, [formula, isEditable]);

    const onClick = useCallback(() => {
        if (!isDisabled) {
            messageContentUpdate.mutate({
                id: currentMessageContentId!,
                is_label_matching_enabled:
                    !formula?.is_labeled_matching_enabled,
            });
        }
    }, [formula, messageContentUpdate, isDisabled, currentMessageContentId]);

    return (
        <>
            {showLockUnLockIcon && (
                <FormulaEditorToolbarIcon
                    onIconClick={onClick}
                    isDisabled={isDisabled}
                    Icon={
                        formula?.is_labeled_matching_enabled
                            ? ICONS.lock
                            : ICONS.unLock
                    }
                    showDot={hasWarning}
                    title={
                        <>
                            <div>Label sync </div>
                            <div>
                                You have multiple components of the same type.
                                They can be synchronised by label for each user
                                (by default) or independent.
                            </div>
                        </>
                    }
                />
            )}
        </>
    );
};

export default FormulaLockUnlockMain;
